/** Wall-clock <-> instant conversion via Intl (no tz library). Second precision. */
export interface WallTime {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

export function wallTimeOf(instantMs: number, timeZone: string): WallTime {
  const parts = formatterFor(timeZone).formatToParts(new Date(instantMs));
  const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute'), second: get('second') };
}

function asUtc(w: WallTime): number {
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
}

function offsetAt(instantMs: number, timeZone: string): number {
  const whole = Math.floor(instantMs / 1000) * 1000;
  return asUtc(wallTimeOf(whole, timeZone)) - whole;
}

export function instantOfWallTime(wall: WallTime, timeZone: string): number {
  const guess = asUtc(wall);
  const first = guess - offsetAt(guess, timeZone);
  return guess - offsetAt(first, timeZone);
}

const WALL_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/;

export function parseWallTime(text: string): WallTime | null {
  const m = WALL_RE.exec(text);
  if (!m) return null;
  const n = (i: number): number => (m[i] === undefined ? 0 : Number(m[i]));
  const wall = { year: n(1), month: n(2), day: n(3), hour: n(4), minute: n(5), second: n(6) };
  if (wall.month < 1 || wall.month > 12 || wall.hour > 23 || wall.minute > 59 || wall.second > 59) return null;
  const probe = new Date(Date.UTC(wall.year, wall.month - 1, wall.day));
  if (probe.getUTCMonth() !== wall.month - 1 || probe.getUTCDate() !== wall.day) return null;
  return wall;
}

export function dayOfYear(wall: WallTime): number {
  return (Date.UTC(wall.year, wall.month - 1, wall.day) - Date.UTC(wall.year, 0, 1)) / 86_400_000 + 1;
}
